// Shared helpers used by every DML-shape extractor (INSERT / UPDATE / DELETE
// / MERGE). Pulled out of the per-kind files to keep them under budget and to
// make the helper surface easy to find.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — DML.

import type { IdentifierRef, ParseOptions, QualifiedName } from '../../sql-grammar/model';
import { rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import { foldIdentifier, readIdentifier, readQualifiedName } from '../scope/identifiers';
import { skipContextFor } from '../classify/tokenSkip';
import type { AliasBinding, FromItem, QueryStructure, UpdateAssignment } from '../types';

import { findTopLevelCommas, trimRange, type TopKeyword } from './scanner';
import type { SelectExtractCtx } from './extractQueryStructure.select';

export type MutableQueryStructure = { -readonly [K in keyof QueryStructure]?: QueryStructure[K] };

export interface DmlTargetRead {
  readonly item: FromItem;
  readonly endOffset: number;
}

// Read a DML target: qualified relation name + optional alias. The alias
// follows the same `[AS] ident` form as FROM-item aliases — but we reject
// clause-keywords here so `UPDATE t SET …` doesn't read 'SET' as an alias.
// eslint-disable-next-line complexity
export function readDmlTarget(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): DmlTargetRead | null {
  const { source, options, lineIdx } = ctx;
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  const qn = readQualifiedName(source, i, end, options);
  if (!qn) return null;
  let p = qn.range.endOffset;
  let alias: AliasBinding | undefined;

  let q = p;
  while (q < end && /\s/.test(source[q])) q++;
  let aliasIdent = readIdentifier(source, q, end, options);
  if (aliasIdent && !aliasIdent.quoted && aliasIdent.value.toUpperCase() === 'AS') {
    let r = aliasIdent.end;
    while (r < end && /\s/.test(source[r])) r++;
    aliasIdent = readIdentifier(source, r, end, options);
  } else if (aliasIdent && !aliasIdent.quoted && isClauseStop(aliasIdent.value)) {
    aliasIdent = null;
  }
  if (aliasIdent && !aliasIdent.quoted && isClauseStop(aliasIdent.value)) aliasIdent = null;

  if (aliasIdent) {
    alias = {
      alias: aliasIdent.value,
      aliasFolded: foldIdentifier(aliasIdent.value, aliasIdent.quoted, options),
      target: qn.qualified,
      source: 'from',
      range: rangeFromOffsets(lineIdx, aliasIdent.start, aliasIdent.end),
    };
    p = aliasIdent.end;
  }

  const item: FromItem = {
    range: rangeFromOffsets(lineIdx, i, p),
    ...(alias ? { alias } : {}),
    target: {
      kind: 'relation',
      qualified: qn.qualified,
      nameRange: rangeFromOffsets(lineIdx, qn.terminalRange.startOffset, qn.terminalRange.endOffset),
      partCount: qn.partCount,
    },
  };
  return { item, endOffset: p };
}

function isClauseStop(value: string): boolean {
  const u = value.toUpperCase();
  return (
    u === 'SET' || u === 'VALUES' || u === 'WHERE' || u === 'USING'
    || u === 'ON' || u === 'JOIN' || u === 'INNER' || u === 'LEFT'
    || u === 'RIGHT' || u === 'FULL' || u === 'CROSS' || u === 'OUTER'
    || u === 'NATURAL' || u === 'STRAIGHT_JOIN' || u === 'LATERAL'
    || u === 'FROM' || u === 'RETURNING' || u === 'DEFAULT' || u === 'SELECT'
    || u === 'WHEN' || u === 'OUTPUT' || u === 'LIMIT' || u === 'OFFSET'
    // MySQL `DELETE FROM t ORDER BY … LIMIT n` / SQL Server `UPDATE t WITH
    // (ROWLOCK) SET …` — the clause keyword, not an alias.
    || u === 'ORDER' || u === 'WITH'
  );
}

// Parse `col1 = expr1, col2 = expr2, …` over [start, end). Used by UPDATE …
// SET, MERGE … WHEN MATCHED THEN UPDATE SET …, and INSERT … ON CONFLICT DO
// UPDATE SET ….
export function parseAssignmentsRange(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  start: number,
  end: number,
): readonly UpdateAssignment[] {
  const trimmed = trimRange(source, start, end, options);
  if (trimmed.startOffset >= trimmed.endOffset) return [];
  const commas = findTopLevelCommas(source, trimmed.startOffset, trimmed.endOffset, options);
  const out: UpdateAssignment[] = [];
  let s = trimmed.startOffset;
  const push = (a: number, b: number): void => {
    const t = trimRange(source, a, b, options);
    if (t.startOffset >= t.endOffset) return;
    const eqIdx = findTopLevelEquals(source, options, t.startOffset, t.endOffset);
    if (eqIdx < 0) return;
    const targetTrim = trimRange(source, t.startOffset, eqIdx, options);
    const valueTrim = trimRange(source, eqIdx + 1, t.endOffset, options);
    const ref = buildColumnRef(source, options, lineIdx, targetTrim.startOffset, targetTrim.endOffset);
    if (!ref) return;
    out.push({
      range: rangeFromOffsets(lineIdx, t.startOffset, t.endOffset),
      target: ref,
      valueText: source.slice(valueTrim.startOffset, valueTrim.endOffset),
    });
  };
  for (const c of commas) { push(s, c); s = c + 1; }
  push(s, trimmed.endOffset);
  return out;
}

function findTopLevelEquals(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): number {
  const ctx = skipContextFor(options);
  let depth = 0;
  let i = start;
  while (i < end) {
    const ident = readIdentifier(source, i, end, options);
    if (ident && (ident.quoted || /[A-Za-z_]/.test(source[i]))) { i = ident.end; continue; }
    const c = source[i];
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    if (c === '=' && depth === 0) return i;
    void ctx;
    i++;
  }
  return -1;
}

export function readColumnListInner(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): readonly IdentifierRef[] {
  const { source, options, lineIdx } = ctx;
  const commas = findTopLevelCommas(source, start, end, options);
  const cols: IdentifierRef[] = [];
  let s = start;
  const push = (a: number, b: number): void => {
    const t = trimRange(source, a, b, options);
    if (t.startOffset >= t.endOffset) return;
    const ref = buildColumnRef(source, options, lineIdx, t.startOffset, t.endOffset);
    if (ref) cols.push(ref);
  };
  for (const c of commas) { push(s, c); s = c + 1; }
  push(s, end);
  return cols;
}

export function buildColumnRef(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  start: number,
  end: number,
): IdentifierRef | null {
  const ident = readIdentifier(source, start, end, options);
  if (!ident) return null;
  const qualified: QualifiedName = {
    name: ident.value,
    raw: source.slice(start, end),
    quoted: ident.quoted,
    foldedName: foldIdentifier(ident.value, ident.quoted, options),
  };
  return {
    qualified,
    range: rangeFromOffsets(lineIdx, start, end),
    kind: 'column',
  };
}

// Pick the smallest start-offset across `indices` (negative ones skipped) or
// `fallback` when none match.
export function pickFirstStart(
  kws: readonly TopKeyword[],
  indices: readonly number[],
  fallback: number,
): number {
  let best = fallback;
  for (const idx of indices) {
    if (idx >= 0 && kws[idx].start < best) best = kws[idx].start;
  }
  return best;
}
